import { useState, useEffect } from "react";
import { TbX } from "react-icons/tb";
import { IoCheckmarkCircle } from "react-icons/io5";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import { useBottomSheetAnimation, BOTTOM_SHEET_ANIMATION_MS } from "../hooks/useBottomSheetAnimation";
import { useShippingPageContext } from "../providers/ShippingPageProvider";
import { PharmacyPickerProvider, PharmacySearch, PharmacyMap, PharmacyCard } from "../../../../components/PharmacyPicker";
import { SELF_PICKUP_PAYMENT_MODELS } from "../constants";
import useIsMobile from "../../../../customHooks/useIsMobile";
import * as translator from "../../../../utils/translator";
import { MainTracker } from "../../../../PixelTrackers/MainTracker";
import { useCartContext } from "../../../../customHooks/useCart";
import "../styles/SelfPickupPharmacySheet.css";

import BenefitCheckIcon from "../assets/benefit-check-icon.svg";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const SelfPickupPharmacySheet: React.FC<Props> = ({ isOpen, onClose }) => {
  const {
    language,
    rxSelfPickup,
    selectedPickupPharmacyCode,
    setSelectedPickupPharmacyCode,
    rxSelfPickupServiceOption,
    setRxSelfPickupServiceOption,
    isRxPickupFastApproval,
    setIsRxPickupFastApproval,
    handleDeliveryOptionChange,
    isReturningCustomer,
  } = useShippingPageContext();

  const { updateCart, getCart } = useCartContext();

  const isSubscription = Boolean(getCart().subscription_plan_id);

  const rxSelfPickupDefaultDeliveryOption = isSubscription ? SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE : rxSelfPickupServiceOption;

  const rawPharmacyCards = rxSelfPickupDefaultDeliveryOption === SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY
    ? rxSelfPickup.prescriptionOnlyPharmacyCards
    : rxSelfPickup.pharmacyCards;

  // When the customer chose a subscription plan, only show pharmacies whose stock supports renewals.
  const activePharmacyCards = isSubscription
    ? Object.fromEntries(Object.entries(rawPharmacyCards).filter(([, card]) => card.for_subscription))
    : rawPharmacyCards;

  // Pay-once (Einmalig bezahlen) is only shown when partner pharmacies exist.
  // When subscription is active, only count pharmacies whose stock supports renewals.
  const payOnceCards = isSubscription
    ? Object.values(rxSelfPickup.pharmacyCards).filter((card) => card.for_subscription)
    : Object.values(rxSelfPickup.pharmacyCards);
  const hasPayOncePharmacies = payOnceCards.length > 0;

  // Auto-switch to prescription-only if there are no pay-once pharmacies.
  useEffect(() => {
    if (!hasPayOncePharmacies && rxSelfPickupServiceOption === SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE) {
      setRxSelfPickupServiceOption(SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY);
      setSelectedPickupPharmacyCode(null);
    }
  }, [hasPayOncePharmacies]);


  useEffect(() => {
    if (!isOpen) return;
    MainTracker.track("custom", "Funnel Choose Pharmacy Popup", {
      customer: isReturningCustomer ? "RP" : "FTB",
    });
  }, [isOpen]);

  const isMobile = useIsMobile();
  const { shouldRender, isClosing } = useBottomSheetAnimation(isOpen, isMobile);
  const [highlightedStockCode, setHighlightedStockCode] = useState<string | null>(null);
  // Google Maps must not initialize while the bottom-sheet animation is running — a translateY
  // container causes the API to calculate visible area as 0 and skip loading tiles. Mount the
  // map only after the animation has fully completed.
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setMapReady(false);
      return;
    }
    const t = setTimeout(() => setMapReady(true), BOTTOM_SHEET_ANIMATION_MS + 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  const t = (key: string, vars?: Record<string, string>) => {
    let result = translator.translate(language, "shipping", key) ?? "";
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        result = result.replace(`{${k}}`, v);
      });
    }
    return result;
  };

  const handlePaymentModelChange = (model: string) => {
    setRxSelfPickupServiceOption(model);
    setSelectedPickupPharmacyCode(null);
  };

  const handleSave = async () => {
    if (!selectedPickupPharmacyCode) return;
    const pharmacy = activePharmacyCards[selectedPickupPharmacyCode];
    await handleDeliveryOptionChange("self_pickup", undefined, {
      selfPickupPharmacyID: pharmacy?.pharmacy_id,
      selfPickupPaymentModel: rxSelfPickupDefaultDeliveryOption,
      isExpressPrescriptionReview: isRxPickupFastApproval,
    });
    onClose();
  };

  if (!shouldRender) return null;

  const animClass = isMobile
    ? (isClosing ? "animate-bottom-sheet-out" : "animate-bottom-sheet-in")
    : (isClosing ? "animate-fade-out-down" : "animate-fade-in-up");

  const rootClasses = [
    "self-pickup-sheet",
    isMobile ? "self-pickup-sheet--mobile" : "self-pickup-sheet--desktop",
    animClass,
  ].join(" ");

  return (
    <PortaledModal shouldShowModal={shouldRender} hideModal={onClose} shouldBackgroundClickHideModal={true}>
      <PharmacyPickerProvider value={{ selectedService: rxSelfPickupDefaultDeliveryOption, selectedStockCode: selectedPickupPharmacyCode }}>
        <div className={rootClasses}>

          {/* Header */}
          <div className="self-pickup-sheet__header">
            <span className="self-pickup-sheet__title">{t("RxSelfPickup/sheet_title")}</span>
            <button onClick={onClose} className="self-pickup-sheet__close" aria-label="close">
              <TbX size={20} color="#333" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="self-pickup-sheet__body">

            {/* Payment model radios — always shown when multiple models available; pay-once tab hidden when no pay-once pharmacies */}
            {rxSelfPickup.availablePaymentModels.length > 1 && (
              <div className="self-pickup-sheet__models">
                {[SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY, SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE].filter(model => {
                  if (model === SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE && !hasPayOncePharmacies) return false;
                  if (model === SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY && isSubscription) return false;
                  return true;
                }).map(model => {
                  const isSelected = rxSelfPickupDefaultDeliveryOption === model;
                  const isPayOnce = model === SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE;
                  const titleKey = isPayOnce
                    ? "RxSelfPickup/model_pay_once_title"
                    : "RxSelfPickup/model_prescription_only_title";
                  const subtitleKey = isPayOnce
                    ? "RxSelfPickup/model_pay_once_subtitle"
                    : "RxSelfPickup/model_prescription_only_subtitle";
                  return (
                    <div
                      key={model}
                      onClick={() => handlePaymentModelChange(model)}
                      className={`self-pickup-sheet__model${isSelected ? " self-pickup-sheet__model--selected" : ""}`}
                    >
                      {/* Row 1: radio + title on the same line */}
                      <div className="self-pickup-sheet__model-row">
                        {isSelected ? (
                          <IoCheckmarkCircle size={20} color="#037D73" style={{ flexShrink: 0 }} />
                        ) : (
                          <div className="self-pickup-sheet__radio" />
                        )}
                        <div
                          className="self-pickup-sheet__model-title"
                          style={{ color: isSelected ? "#037d73" : "#616975", fontWeight: isSelected ? 600 : 500 }}
                        >
                          {t(titleKey)}
                        </div>
                      </div>
                      {/* Rows below: subtitle + expanded content, full card width */}
                      <div className="self-pickup-sheet__model-subtitle">{t(subtitleKey)}</div>
                      {isSelected && isPayOnce && (
                        <div className="self-pickup-sheet__pay-once-body">
                          <p className="self-pickup-sheet__pay-once-desc">{t("RxSelfPickup/option_row_description")}</p>
                          <div className="self-pickup-sheet__benefits">
                            {["RxSelfPickup/benefit_pay_once", "RxSelfPickup/benefit_reserved", "RxSelfPickup/benefit_skip_line"].map(key => (
                              <div key={key} className="self-pickup-sheet__benefit">
                                <img src={BenefitCheckIcon} alt="" style={{ flexShrink: 0 }} />
                                <span>{t(key)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Fast Approval toggle — hidden when no express doctor is available */}
            {rxSelfPickup.fastApprovalAvailable && (
              <div className="self-pickup-sheet__fast-approval">
                <div className="self-pickup-sheet__fast-approval-left">
                  <span className="self-pickup-sheet__fast-badge">NEU</span>
                  <div>
                    <div className="self-pickup-sheet__fast-title">{t("RxSelfPickup/fast_approval_title")}</div>
                    <div className="self-pickup-sheet__fast-desc">
                      {t("RxSelfPickup/fast_approval_description", { price: `€${rxSelfPickup.fastApprovalPrice}` })}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const newVal = !isRxPickupFastApproval;
                    setIsRxPickupFastApproval(newVal);
                    updateCart({ is_express_prescription_review: newVal });
                  }}
                  className={`self-pickup-sheet__toggle${isRxPickupFastApproval ? " self-pickup-sheet__toggle--on" : ""}`}
                  aria-label={t("RxSelfPickup/fast_approval_title")}
                >
                  <div className="self-pickup-sheet__toggle-knob" />
                </button>
              </div>
            )}

            {selectedPickupPharmacyCode ? (
              /* Pharmacy chosen — show the selected card, hide search + map */
              <div className="px-4">
                <PharmacyCard
                  pharmacyId={selectedPickupPharmacyCode}
                  pharmacy={activePharmacyCards[selectedPickupPharmacyCode] ?? null}
                  onCancel={() => setSelectedPickupPharmacyCode(null)}
                />
              </div>
            ) : (
              /* No pharmacy chosen — show search header, list and map */
              <>
                <div className="self-pickup-sheet__search-title">
                  {t("RxSelfPickup/search_by_pharmacy", { count: String(Object.keys(activePharmacyCards).length) })}
                </div>

                <PharmacySearch
                  stockLocations={activePharmacyCards}
                  onPharmacySelect={setSelectedPickupPharmacyCode}
                  onPharmacyHighlight={setHighlightedStockCode}
                  highlightedStockCode={highlightedStockCode}
                  hideProductsCount
                />

                {/* Map — only mounts after the open animation completes */}
                <div className="self-pickup-sheet__map-wrap">
                  {mapReady ? (
                    <PharmacyMap
                      allPharmaciesLocations={activePharmacyCards}
                      selectedStockCode={selectedPickupPharmacyCode}
                      onPharmacySelect={setSelectedPickupPharmacyCode}
                      highlightedStockCode={highlightedStockCode}
                      onPharmacyHighlight={setHighlightedStockCode}
                      height={280}
                      showSelectButton={true}
                    />
                  ) : (
                    <div style={{ height: 280 }} />
                  )}
                </div>
              </>
            )}

          </div>

          {/* Footer */}
          <div className="self-pickup-sheet__footer">
            <button
              onClick={handleSave}
              disabled={!selectedPickupPharmacyCode}
              className="self-pickup-sheet__save"
            >
              {t("RxSelfPickup/save")}
            </button>
          </div>

        </div>
      </PharmacyPickerProvider>
    </PortaledModal>
  );
};

export default SelfPickupPharmacySheet;
